import * as React from 'react';

import classnames from 'classnames';
import numeral from 'numeral';
// @ts-expect-error
import {Bar, BarChart, CartesianGrid, Tooltip, XAxis, YAxis, ResponsiveContainer} from 'recharts';
import _ from 'underscore';

import type {ReportFacet} from 'nutshell-core/types';

import {colors} from 'shells/colors/colors';
import {CloudDownloadIcon} from 'shells/icon';
// @ts-expect-error
import {getInstanceCurrencyPrefix} from 'shells/utils/currency';
// @ts-expect-error
import {truncateListWithSummary} from 'shells/utils/list';
// @ts-expect-error
import {smartTruncate} from 'shells/utils/string';

import {ChartEmptyState} from './empty-state/chart-empty-state';

// @ts-expect-error
import {validTickLabelNodeProps} from './index';

import styles from './card-chart.module.css';

import sharedStyles from '../shared.module.css';

// Leaving all animations false for now, until we have more XP w/ this library.
const IS_ANIMATION_ACTIVE = false;

const DOMAIN_PADDING = {top: 30, right: 50, bottom: 30, left: 20} as const;
// ROW_HEIGHT: Enough height to prevent smartly hiding rows. Used in full screen
// chart height calculations.
const ROW_HEIGHT = 27;
const SVG_WRAPPER_STYLES = {margin: '0 auto'} as const;
const X_AXIS_TICK_COUNT = 6;

const CARD_CHART_SUMMARY_MAP_KEYS = ['y1', 'y2'];
const CARD_CHART_SUMMARY_ITEM = {name: 'Other', fill: colors.offWhiteDk} as const;

type Data = Array<{
    name: string;
    y1: number;
    y2: number;
    sourceTypeLabel?: string;
}>;

type Padding = {
    top: number;
    left: number;
    bottom: number;
    right: number;
};

type AxisConfig = {
    yAxisTickWidth: number;
    yAxisLabelLength: number;
    yAxisLabelPosition: number;
};

type TickLabelFontStyles = {
    letterSpacing: number;
    fontSize: number;
};

type CardChartProps = {
    data: Data;
    title?: string;
    facet: ReportFacet;
    exportReportLink?: string;
    onClickChart?: () => any;
    axisConfig: AxisConfig;
    tickLabelFontStyles?: TickLabelFontStyles;
    maxRows?: number;
    padding: Padding;
    actionButtons?: React.ReactNode;
    width?: number;
    height?: number;
    isPercentage?: boolean;
    onBarClick?: (data?: any) => void;
};

export class CardChart extends React.PureComponent<CardChartProps> {
    static rowHeight = ROW_HEIGHT;
    static domainPadding = DOMAIN_PADDING;

    static defaultProps = {
        padding: DOMAIN_PADDING,
    };

    render() {
        const title = this.props.title ? (
            <div className={styles.title}>{this.props.title}</div>
        ) : undefined;
        const chartId = this.props.title ? `chart-${this.props.title.toLowerCase()}` : 'chart';

        if (this.props.data.length === 0) {
            return (
                <div id={chartId} className={styles.container}>
                    {title ? title : undefined}
                    <ChartEmptyState />
                </div>
            );
        }

        let data = this.props.data;
        if (this.props.maxRows) {
            data = truncateListWithSummary(
                data,
                this.props.maxRows,
                CARD_CHART_SUMMARY_MAP_KEYS,
                CARD_CHART_SUMMARY_ITEM
            );
        }

        // Always total if a percentage-based graph
        const reportFacet = this.props.isPercentage ? 'total' : this.props.facet;
        const tickFormatting = this.props.isPercentage
            ? {prefix: null, suffix: '%'}
            : {prefix: getInstanceCurrencyPrefix(), suffix: null};

        return (
            <div
                id={chartId}
                onClick={this.handleContainerClick}
                className={classnames('fade-in-animation', styles.container)}
            >
                {title ? title : undefined}
                {typeof window !== 'undefined' ? (
                    <ResponsiveContainer height='100%' width='100%'>
                        <SegmentedTotalsChart
                            padding={this.props.padding}
                            facet={reportFacet}
                            axisConfig={this.props.axisConfig}
                            data={data}
                            renderTooltip={renderTooltip}
                            tickLabelFontStyles={this.props.tickLabelFontStyles}
                            tickFormatting={tickFormatting}
                            yAxisTickWidth={this.props.axisConfig.yAxisTickWidth}
                            onBarClick={this.props.onBarClick}
                        />
                    </ResponsiveContainer>
                ) : (
                    <SegmentedTotalsChart
                        height={this.props.height}
                        width={this.props.width}
                        padding={this.props.padding}
                        facet={reportFacet}
                        axisConfig={this.props.axisConfig}
                        data={data}
                        renderTooltip={renderTooltip}
                        tickLabelFontStyles={this.props.tickLabelFontStyles}
                        tickFormatting={tickFormatting}
                        yAxisTickWidth={this.props.axisConfig.yAxisTickWidth}
                        onBarClick={this.props.onBarClick}
                    />
                )}
                {this.renderActions()}
            </div>
        );
    }

    renderActions = () => {
        return (
            <div className={styles['chart-actions']}>
                {this.props.actionButtons}
                {this.props.exportReportLink ? (
                    <a
                        key='download'
                        className={sharedStyles['download-icon']}
                        href={this.props.exportReportLink}
                        data-title='Download CSV'
                        download={true}
                        onClick={this.handleClickDownloadExport}
                    >
                        <CloudDownloadIcon />
                    </a>
                ) : null}
            </div>
        );
    };

    handleClickDownloadExport = (e: React.SyntheticEvent) => {
        // Stop propagation so that we don't trigger handleClickFullScreen
        e.stopPropagation();
    };

    handleContainerClick = (e: React.SyntheticEvent) => {
        if (!e.nativeEvent.defaultPrevented && typeof this.props.onClickChart === 'function') {
            this.props.onClickChart();
        }
    };
}

export const renderTooltip = (props: {
    active: boolean;
    payload: {
        payload: Array<Data>;
    };
    facet: ReportFacet;
    label: string;
}) => {
    if (!props.active) return null;

    const currencyPrefix = getInstanceCurrencyPrefix();
    // @ts-expect-error
    const {payload} = props.payload[0];
    const format = '0,0[.]0';
    const lines = [
        `${currencyPrefix && !payload.isPercent ? currencyPrefix : ''}${numeral(payload.y1).format(
            format
        )}${payload.isPercent ? '%' : ''}`,
        `${numeral(payload.y2).format(format)} leads`,
    ];

    if (props.facet === 'count') {
        lines.reverse();
    }

    const sourceTypeLabel = payload.sourceTypeLabel;

    return (
        <div className={sharedStyles.tooltip}>
            <div>{props.label}</div>
            {sourceTypeLabel ? (
                <div className={styles['tooltip-secondary-item']}>{sourceTypeLabel}</div>
            ) : undefined}
            {lines.map((line, index) => {
                const styleName =
                    index === 0 ? styles['tooltip-primary-item'] : styles['tooltip-secondary-item'];

                return (
                    <div key={`line-${line}`} className={styleName}>
                        {line}
                    </div>
                );
            })}
        </div>
    );
};

// @ts-expect-error
const getDataKey = (facet) => {
    return facet === 'count' ? 'y2' : 'y1';
};

/**
 * Calculate how many x-axis ticks should be rendered. If
 * the cartesian's x-axis domain ceiling is less than the default tick count,
 * reduce the x-axis tick count to the ceiling of the domain. This makes the
 * chart easier to read by preventing tick labels/values from duplicating or
 * reducing them into small fractions.
 *
 * @param  {Array}  data            An Array of objects.
 * @param  {String} key             Data item key used to access x-axis values.
 * @param  {Number} defaultCount    default count of ticks/axes.
 * @return {Number} Return the default tick count or the data domain ceiling.
 */
// @ts-expect-error
const getXAxisTickCount = (data, key, defaultCount) => {
    // @ts-expect-error
    const values = data.map((item) => item[key]);
    const max = Math.max(...values);

    const result = max && max < defaultCount ? Math.ceil(max) : defaultCount;

    return result;
};

export const renderYAxisTickLabel = (
    axisConfig: AxisConfig,
    tickLabelFontStyles?: TickLabelFontStyles
) => {
    type YAxisTickLabelProps = {
        payload: {
            value: string;
        };
    };

    function YAxisTickLabel(props: YAxisTickLabelProps) {
        const value = props.payload.value;

        const {yAxisLabelLength, yAxisLabelPosition} = axisConfig;

        const result =
            value && typeof value === 'string'
                ? smartTruncate(value, yAxisLabelLength, yAxisLabelPosition)
                : value;
        const validNodeProps = _.pick(props, validTickLabelNodeProps);

        return (
            <text
                {...validNodeProps}
                style={tickLabelFontStyles}
                fill={colors.greyDk}
                title={value}
                dy='3.5px'
            >
                {result}
            </text>
        );
    }

    return YAxisTickLabel;
};

// TODO: (Steve) De-dup this code that is also in hero-chart.
// I tried to componentize this label (numeral-tick-label.js) rendering
// but was getting a lot of errors in the process with recharts.
// Tabling this for now and will come back.
export const renderXAxisTickLabel = (opts: {
    usePrefix: boolean;
    tickLabelFontStyles?: TickLabelFontStyles;
    tickFormatting: {
        prefix: string | null | undefined;
        suffix: string | null | undefined;
    };
    facet: ReportFacet;
}) => {
    const defaultFormat = '0[.]0a';
    const options = {
        format: defaultFormat,
        // @ts-expect-error
        usePrefix: false,
        suffix: opts.tickFormatting.suffix || false,
        ...opts,
    } as const;
    const currencyPrefix = opts.facet === 'count' ? '' : opts.tickFormatting.prefix;

    type XAxisTickLabelProps = {
        payload: {
            value: number;
        };
    };

    function XAxisTickLabel(props: XAxisTickLabelProps) {
        const value = props.payload.value;
        const numeralFormat = numeral(value).format(options.format);
        const validNodeProps = _.pick(props, validTickLabelNodeProps);

        let result = '';
        if (value !== 0) {
            if (options.tickFormatting.suffix) {
                result = options.tickFormatting.suffix
                    ? numeralFormat + options.tickFormatting.suffix
                    : numeralFormat;
            } else {
                result = options.usePrefix ? currencyPrefix + numeralFormat : numeralFormat;
            }
        }

        return (
            <text
                {...validNodeProps}
                fill={colors.greyLt}
                dy='3.5px'
                style={opts.tickLabelFontStyles}
            >
                {result}
            </text>
        );
    }

    return XAxisTickLabel;
};

type SegmentedTotalsChartProps = {
    data: Data;
    facet: ReportFacet;
    padding: Padding;
    axisConfig: AxisConfig;
    tickLabelFontStyles?: TickLabelFontStyles;
    tickFormatting: {
        prefix: string | null | undefined;
        suffix: string | null | undefined;
    };
    width?: number;
    height?: number;
    renderTooltip: (...args: any) => any;
    yAxisTickWidth: number;
    onBarClick?: (data?: any) => void;
};

export function SegmentedTotalsChart(props: SegmentedTotalsChartProps) {
    const xAxisTickCount = getXAxisTickCount(
        props.data,
        getDataKey(props.facet),
        X_AXIS_TICK_COUNT
    );

    return (
        <BarChart
            height={props.height}
            width={props.width}
            data={props.data} // buckets
            layout='vertical'
            margin={props.padding}
            barCategoryGap='15%'
            style={SVG_WRAPPER_STYLES}
        >
            <CartesianGrid stroke={colors.offWhiteMd} horizontal={false} />
            <Tooltip content={props.renderTooltip} />
            <XAxis
                dataKey={getDataKey(props.facet)}
                type='number'
                axisLine={false}
                tickLine={false}
                orientation='top'
                tickCount={xAxisTickCount}
                tick={renderXAxisTickLabel({
                    usePrefix: true,
                    tickLabelFontStyles: props.tickLabelFontStyles,
                    facet: props.facet,
                    tickFormatting: props.tickFormatting,
                })}
            />
            <YAxis
                type='category'
                dataKey='name'
                axisLine={false}
                tickLine={false}
                tick={renderYAxisTickLabel(props.axisConfig, props.tickLabelFontStyles)}
                width={props.yAxisTickWidth}
            />
            <Bar
                style={props.onBarClick ? {cursor: 'pointer'} : undefined}
                isAnimationActive={IS_ANIMATION_ACTIVE}
                dataKey={getDataKey(props.facet)}
                onClick={props.onBarClick}
            />
        </BarChart>
    );
}
